import React, { useState, useEffect } from "react";
import { PKG_PRICE_LABELS } from "../../utils/constants";
import "./MatrixTree.css";

const PAGE_SIZE = 20;

export default function MatrixTree({ userId, planContract, dataContract, maxLevels, showUplines = true, pkgIndex, pkgCount }) {
  const [uplines, setUplines] = useState([]);
  const [levelCounts, setLevelCounts] = useState([]);
  const [levelUsers, setLevelUsers] = useState({});
  const [levelPages, setLevelPages] = useState({});
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [loadingLevel, setLoadingLevel] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  const hasPkg = pkgIndex !== undefined;

  useEffect(() => {
    if (userId && planContract) loadInitial();
    // eslint-disable-next-line
  }, [userId, planContract, pkgIndex]);

  async function loadInitial() {
    setInitialLoading(true);
    setLevelUsers({});
    setLevelPages({});
    setExpandedLevel(null);
    try {
      const [counts, uplinesArr] = await Promise.all([
        hasPkg ? planContract.getMatrixLevelCounts(userId, pkgIndex) : planContract.getMatrixLevelCounts(userId),
        hasPkg ? planContract.getMatrixUplines(userId, pkgIndex) : planContract.getMatrixUplines(userId),
      ]);
      setLevelCounts(counts.map((c) => c.toNumber()));
      setUplines(uplinesArr.map((u) => u.toNumber()).filter((u) => u > 0));
    } catch (err) {
      console.error("MatrixTree loadInitial:", err);
    } finally {
      setInitialLoading(false);
    }
  }

  async function loadLevelPage(level, page) {
    setLoadingLevel(level);
    try {
      const res = hasPkg
        ? await planContract.getMatrixLevelUsers(userId, pkgIndex, level, page, PAGE_SIZE)
        : await planContract.getMatrixLevelUsers(userId, level, page, PAGE_SIZE);

      const ids = res.ids.map((id) => id.toNumber()).filter((id) => id > 0);
      const users = await fetchUserDetails(ids);
      setLevelUsers((prev) => ({ ...prev, [level]: users }));
      setLevelPages((prev) => ({ ...prev, [level]: page }));
    } catch (err) {
      console.error("loadLevelPage error:", err);
      setLevelUsers((prev) => ({ ...prev, [level]: [] }));
    } finally {
      setLoadingLevel(null);
    }
  }

  async function fetchUserDetails(ids) {
    if (!ids.length || !dataContract) return [];
    const batch = Math.min(ids.length, 20);
    const results = await Promise.all(
      ids.slice(0, batch).map(async (uid) => {
        try {
          const [info, pkgs] = await Promise.all([
            dataContract.getUserInfo(uid),
            planContract.getAllPackages(uid),
          ]);
          const count = pkgCount || 8;
          const activePrices = [];
          for (let p = 0; p < count; p++) {
            if (pkgs.a[p]) activePrices.push(PKG_PRICE_LABELS[p] || `$${p}`);
          }
          return { id: uid, name: info.name || "", activePrices };
        } catch {
          return { id: uid, name: "", activePrices: [] };
        }
      })
    );
    return results;
  }

  function toggleLevel(level) {
    if (expandedLevel === level) {
      setExpandedLevel(null);
    } else {
      setExpandedLevel(level);
      if (!levelUsers[level]) loadLevelPage(level, 0);
    }
  }

  const totalTeam = levelCounts.reduce((s, c) => s + c, 0);

  if (initialLoading) {
    return <div className="mt-loading">Loading matrix...</div>;
  }

  return (
    <div className="matrix-tree">
      {showUplines && uplines.length > 0 && (
        <div className="mt-uplines">
          <span className="mt-uplines-label">Your Upline Chain</span>
          <div className="mt-upline-chain">
            {uplines.map((uid, i) => (
              <span key={i} className="mt-upline-badge">{uid}</span>
            ))}
            <span className="mt-upline-badge mt-upline-you">You ({userId})</span>
          </div>
        </div>
      )}

      <div className="mt-levels">
        <div className="mt-levels-header">
          <span className="mt-levels-title">Matrix Levels</span>
          <span className="mt-levels-total">{totalTeam} total</span>
        </div>

        {Array.from({ length: maxLevels }, (_, i) => {
          const isExpanded = expandedLevel === i;
          const isLoading = loadingLevel === i;
          const users = levelUsers[i];
          const count = levelCounts[i] || 0;
          const currentPage = levelPages[i] || 0;
          const totalPages = Math.ceil(count / PAGE_SIZE);

          return (
            <div key={i} className="mt-level-item">
              <div
                className={`mt-level-header ${isExpanded ? "mt-level-expanded" : ""}`}
                onClick={() => toggleLevel(i)}
              >
                <div className="mt-level-left">
                  <span className="mt-level-num">L{i + 1}</span>
                  <span className="mt-level-label">Level {i + 1}</span>
                </div>
                <div className="mt-level-right">
                  <span className="mt-level-count">{count}</span>
                  {isLoading && <span className="mt-level-loading">...</span>}
                  <span className="mt-level-arrow">{isExpanded ? "\u25BC" : "\u25B6"}</span>
                </div>
              </div>

              {isExpanded && (
                <div className="mt-level-body">
                  {isLoading ? (
                    <div className="mt-level-loading-body">Loading...</div>
                  ) : users && users.length > 0 ? (
                    <>
                      <div className="mt-level-users">
                        {users.map((u, j) => (
                          <div key={j} className="mt-user-card">
                            <div className="mt-user-top">
                              <span className="mt-user-name">{u.name || "User"}</span>
                              <span className="mt-user-id">#{u.id}</span>
                            </div>
                            {u.activePrices.length > 0 && (
                              <div className="mt-user-pkgs">
                                {u.activePrices.map((p, k) => (
                                  <span key={k} className="mt-user-pkg">{p}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {totalPages > 1 && (
                        <div className="mt-pagination">
                          <button className="mt-page-btn" disabled={currentPage === 0 || isLoading} onClick={(e) => { e.stopPropagation(); loadLevelPage(i, currentPage - 1); }}>Prev</button>
                          <span className="mt-page-info">{currentPage + 1} / {totalPages}</span>
                          <button className="mt-page-btn" disabled={currentPage >= totalPages - 1 || isLoading} onClick={(e) => { e.stopPropagation(); loadLevelPage(i, currentPage + 1); }}>Next</button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="mt-level-empty">No users</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
